/**
 * Hero Card Widget
 * Full-width gradient hero with KPI pills and user greeting.
 * Moved from StudentDashboardBeta (Phase 3.2 extraction).
 */

import React from 'react'
import { useTranslation } from '../../i18n/TranslationProvider'
import type { BffUser, BffKpis } from '../../types/bff-v2'

export interface HeroWidgetProps {
  user: BffUser
  kpis: BffKpis
  className?: string
}

const HeroWidget: React.FC<HeroWidgetProps> = ({ user, kpis, className = '' }) => {
  const { t } = useTranslation()

  // Null-safe fallbacks — prevents crash when BFF data is missing
  const firstName = user?.first_name || 'Student'
  const avatarInitial = user?.avatar_initial || '?'
  const instrument = user?.instrument || ''
  const level = user?.level || ''
  const safeKpis = kpis || { streak_days: 0, minutes_today: 0, total_lessons: 0, weekly_minutes: 0 }

  type KpiDatum = { value: number; label: string }
  const kpiData: KpiDatum[] = [
    { value: safeKpis.streak_days, label: t('student.day_streak', 'day streak') },
    { value: safeKpis.minutes_today, label: t('student.min_today', 'min today') },
    { value: safeKpis.total_lessons, label: t('student.lessons_total', 'lessons total') },
    { value: safeKpis.weekly_minutes, label: t('student.min_this_week', 'min this week') },
  ]

  return (
    <div className={`relative overflow-hidden rounded-[var(--radius-r)]
                    bg-gradient-to-br from-[#1C2A3A] via-[#1e4060] via-[#2a6278] to-[#3d8a9e]
                    p-[clamp(20px,3.5vw,30px)] mb-[clamp(14px,2.5vw,22px)] text-white ${className}`}>
      {/* Glow orbs */}
      <div className="absolute -top-[60%] -right-[8%] w-[380px] h-[380px]
                      rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(0,198,232,.16), transparent 68%)' }} />
      <div className="absolute -bottom-[40%] -left-[5%] w-[240px] h-[240px]
                      rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(201,168,76,.1), transparent 68%)' }} />

      {/* Content */}
      <div className="relative z-[1] flex items-start gap-4 flex-wrap">
        <div className="w-[52px] h-[52px] rounded-full flex-shrink-0 flex items-center justify-center
                        text-[20px] font-extrabold text-[var(--color-navy)]
                        border-2 border-white/35"
             style={{ background: 'linear-gradient(135deg, var(--color-cyan-light), var(--color-neon))' }}>
          {avatarInitial}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-[clamp(20px,3vw,28px)] font-extrabold tracking-[-.3px] leading-[1.15]">
            {t('dashboard.good_morning', 'Good morning')}, {firstName}
          </h1>
          <p className="text-white/60 text-[11px] font-semibold tracking-[.7px] uppercase mt-[3px]">
            {instrument} · {level}
          </p>
        </div>
      </div>

      {/* KPI pills */}
      <div className="relative z-[1] flex gap-2 mt-[18px] flex-wrap">
        {kpiData.map((kpi, i) => (
          <div key={i}
               className="flex items-center gap-2 bg-white/9 backdrop-blur-[10px]
                          border border-white/12 rounded-[14px] px-[14px] py-[9px]
                          text-[12px] font-semibold">
            <div className="flex flex-col">
              <span className="text-[22px] font-black leading-none" style={{ color: 'var(--color-neon)' }}>
                {kpi.value}
              </span>
              <span className="text-white/50 text-[10px] font-medium mt-[1px] leading-none">
                {kpi.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default HeroWidget
